import * as yup from "yup";
import React, { useEffect, useRef, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { Input } from "./forms/input";
import { useAuthContext } from "@hooks/contexts/auth-context";

import Logo from "./sections/Logo";
import { UserMiniPanel } from "./layout/user-mini-panel";
import { ClientSessionStageEnum } from "@hooks/dtos/IClientSessionDTO";
import { clientIsValidStage } from "@hooks/functions/Client/stage";
import { HEADER_LOGIN_REQUEST_EVENT } from "@hooks/functions/Client/requestHeaderLogin";
import { CircleHelp, Gift, House, LayoutGrid, Ticket, Wallet } from "lucide-react";
import "./Header.css";

interface ILoginFormValues {
  document: string;
}

interface IProps {
  onChange: (params: { primaryInfo: boolean }) => void;
}

export const Header: React.FC<IProps> = ({ onChange }) => {
  const { client, identify } = useAuthContext();

  const [scrolled, setScrolled] = useState(false);
  const [showNavigation, setShowNavigation] = useState(false);
  const headerRef = useRef<HTMLElement>(null);
  const loginFormRef = useRef<HTMLFormElement>(null);
  const location = useLocation();

  const [showCpfInput, setShowCpfInput] = useState(false);

  // const refCampaign = getCampaignAffiliateReferral("ZVBaUw");
  // const refRegister = getPlatformAffiliateReferral();
  // console.log({ refCampaign, refRegister });

  // Login form validation schema
  const loginScheme = yup.object().shape({
    document: yup
      .string()
      .required("Informe seu CPF")
      .matches(/^\d{3}\.\d{3}\.\d{3}-\d{2}$/, "CPF inválido"),
  });

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<ILoginFormValues>({
    resolver: yupResolver(loginScheme) as any,
    mode: "onSubmit",
    reValidateMode: "onBlur",
  });

  const handleLoginClick = () => setShowCpfInput(true);

  useEffect(() => {
    function openLoginFromPage() {
      setShowNavigation(true);
      setShowCpfInput(true);
      headerRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
    }

    window.addEventListener(HEADER_LOGIN_REQUEST_EVENT, openLoginFromPage);
    return () => window.removeEventListener(HEADER_LOGIN_REQUEST_EVENT, openLoginFromPage);
  }, []);

  useEffect(() => {
    if (showCpfInput) loginFormRef.current?.querySelector("input")?.focus();
  }, [showCpfInput]);

  const handleCancel = () => {
    setShowCpfInput(false);
    reset(); // Reset form when canceling
  };

  useEffect(() => {
    if (!client) return;
    setShowCpfInput(false);
    setShowNavigation(false);
    reset();
  }, [client, reset]);

  // Handle login form submission
  async function handleLogin(data: ILoginFormValues) {
    await identify(data);
  }

  // Handle Enter key press for login form
  function handleKeyPress(event: KeyboardEvent) {
    if (event.key === "Enter" && showCpfInput) {
      const activeElement = document.activeElement;
      if (
        activeElement instanceof HTMLInputElement ||
        activeElement instanceof HTMLTextAreaElement
      ) {
        activeElement.blur();
      }
      event.preventDefault();
      handleSubmit(handleLogin)();
    }
  }

  useEffect(() => {
    // Add shadow when scrolling
    const onScroll = () => setScrolled(window.scrollY > 10);
    window.addEventListener("scroll", onScroll);

    // Add keyboard event listener for Enter key
    document.addEventListener("keydown", handleKeyPress);

    return () => {
      window.removeEventListener("scroll", onScroll);
      document.removeEventListener("keydown", handleKeyPress);
    };
  }, [showCpfInput]); // Re-attach listener when showCpfInput changes

  useEffect(() => {
    setShowNavigation(false);
  }, [location.pathname, location.search, location.hash]);

  useEffect(() => {
    if (!showNavigation) return;

    function handlePageClick(event: MouseEvent) {
      if (
        headerRef.current &&
        !headerRef.current.contains(event.target as Node)
      ) {
        setShowNavigation(false);
      }
    }

    document.addEventListener("mousedown", handlePageClick);

    return () => document.removeEventListener("mousedown", handlePageClick);
  }, [showNavigation]);

  const navLinks = [
    { label: "Home", to: "/", icon: <House size={18} /> },
    {
      label: "Meus Tickets",
      to: "/my-tickets",
      icon: <Ticket size={18} />,
      role: [
        ClientSessionStageEnum.VERIFIED,
        ClientSessionStageEnum.IDENTIFIED,
      ],
    },
    {
      label: "Meus Prêmios",
      to: "/my-rewards",
      icon: <Gift size={18} />,
      role: [
        ClientSessionStageEnum.VERIFIED,
        ClientSessionStageEnum.IDENTIFIED,
      ],
    },
    {
      label: "Financeiro",
      to: "/financial",
      icon: <Wallet size={18} />,
      role: [
        ClientSessionStageEnum.VERIFIED,
        ClientSessionStageEnum.IDENTIFIED,
      ],
    },
    { label: "Campanhas", to: "/campaign/default/list", icon: <LayoutGrid size={18} /> },
    { label: "Ajuda", to: "#", icon: <CircleHelp size={18} /> },
  ];

  return (
    <header
      ref={headerRef}
      className={`raffle-header bg-white border-bottom ${scrolled ? "shadow-sm" : ""}`}
      style={{
        backdropFilter: "blur(10px)",
        WebkitBackdropFilter: "blur(10px)",
        position: "sticky",
        top: 0,
        zIndex: 100,
      }}
    >
      <div className="container">
        <nav className="navbar navbar-expand-lg px-3 px-lg-0">
          {/* Logo */}
          <Link
            to="/"
            className="navbar-brand"
            onClick={() => setShowNavigation(false)}
          >
            <Logo imgSize="xl" txtSize="md" />
          </Link>

          {/* Toggle */}
          <button
            className="navbar-toggler ms-auto"
            type="button"
            aria-controls="mainNav"
            aria-expanded={showNavigation}
            aria-label="Alternar menu de navegação"
            onClick={() => setShowNavigation((current) => !current)}
          >
            <span className="navbar-toggler-icon"></span>
          </button>

          <div
            className={`header-nav collapse navbar-collapse px-2 px-lg-0 pt-3 pt-lg-0 ${
              showNavigation ? "show" : ""
            }`}
            id="mainNav"
          >
            {/* Nav links */}
            <ul className="navbar-nav me-auto mb-3 mb-lg-0">
              {navLinks.map((link, index) => {
                // --- Check if can see the Menu Option --- //
                const canSeeMenu = clientIsValidStage(
                  client?.stage,
                  link?.role || [],
                );
                if (!canSeeMenu) return null;

                return (
                  <li className="nav-item" key={`${link.to}/${index}`}>
                    <Link
                      to={link.to}
                      className={`nav-link fw-semibold ${
                        location.pathname === link.to
                          ? "active text-warning"
                          : ""
                      }`}
                      onClick={() => setShowNavigation(false)}
                      aria-current={location.pathname === link.to ? "page" : undefined}
                    >
                      <span className="header-nav-content">
                        <span className="header-nav-icon d-lg-none" aria-hidden="true">{link.icon}</span>
                        <span>{link.label}</span>
                      </span>
                    </Link>
                  </li>
                );
              })}
            </ul>

            {/* Buttons container */}
            <div className="header-actions d-flex flex-column flex-lg-row gap-2 align-items-center">
              {!showCpfInput ? (
                <>
                  {/* Not logged | identified */}
                  {!client ? (
                    <>
                      {/* Login */}
                      <button
                        className="btn btn-outline-warning fw-semibold px-4"
                        style={{ borderRadius: "50px" }}
                        onClick={handleLoginClick}
                      >
                        Entrar
                      </button>

                      {/* Register */}
                      <button
                        onClick={handleLoginClick}
                        className="btn btn-warning text-white fw-semibold d-flex align-items-center justify-content-center gap-2"
                        style={{ borderRadius: "50px" }}
                      >
                        Cadastrar
                        <svg
                          width="14"
                          height="14"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2.5"
                        >
                          <path d="M5 12h14M12 5l7 7-7 7" />
                        </svg>
                      </button>
                    </>
                  ) : (
                    <>{<UserMiniPanel client={client} />}</>
                  )}
                </>
              ) : (
                <form
                  ref={loginFormRef}
                  onSubmit={(e) => {
                    e.preventDefault();
                    handleSubmit(handleLogin)();
                  }}
                  className="header-login-form d-flex flex-column flex-lg-row gap-2 align-items-center fade show"
                >
                  {/* CPF Input with validation */}
                  <Input
                    animated
                    label="CPF"
                    placeholder="000.000.000-00"
                    type="text"
                    mask="cpf"
                    register={register("document")}
                    errorMessage={String(errors?.document?.message || "")}
                  />

                  {/* Submit */}
                  <button
                    type="submit"
                    className="btn btn-primary fw-semibold px-4"
                    style={{ borderRadius: "50px" }}
                  >
                    Enviar
                  </button>

                  {/* Cancel */}
                  <button
                    type="button"
                    className="btn btn-outline-secondary fw-semibold px-4"
                    style={{ borderRadius: "50px" }}
                    onClick={handleCancel}
                  >
                    Cancelar
                  </button>
                </form>
              )}
            </div>
          </div>
        </nav>
      </div>
    </header>
  );
};
